import React, { useState, useEffect } from 'react';
import { useTheme as useAppTheme } from '@/context/ThemeContext';
import i18n from '@/i18n';
import { PREFS_KEY, defaultPrefs } from '../constants';

export type Prefs = typeof defaultPrefs;

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...defaultPrefs, ...JSON.parse(raw) };
  } catch {
    return { ...defaultPrefs };
  }
  return { ...defaultPrefs };
}

function savePrefs(prefs: Prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function usePrefs() {
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const { setThemeMode } = useAppTheme();

  useEffect(() => {
    savePrefs(prefs);
    if (prefs.theme) setThemeMode(prefs.theme);
    if (prefs.language && i18n.language !== prefs.language) i18n.changeLanguage(prefs.language);
  }, [prefs, setThemeMode]);

  const handleChange = (key: keyof Prefs) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (key === 'notificationsEnabled' && value === true && 'Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          setPrefs((p) => ({ ...p, [key]: true }));
        } else {
          setPrefs((p) => ({ ...p, [key]: false }));
          alert('Permissão de notificação negada. Nas configurações do navegador ative para receber lembretes.');
        }
      });
      return;
    }
    setPrefs((p) => {
      const newPrefs = { ...p, [key]: value };
      if (key === 'theme') setThemeMode(value);
      if (key === 'language' && i18n.language !== value) i18n.changeLanguage(value);
      return newPrefs;
    });
  };

  const playNotificationSound = () => {
    if (!prefs.notificationsEnabled) return;
    if (prefs.notificationsBeep) {
      const AudioCtx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = 880;
      o.connect(ctx.destination);
      o.start();
      setTimeout(() => { o.stop(); ctx.close(); }, 200);
    } else if (prefs.notificationsSound) {
      const audio = new Audio(prefs.notificationsSound);
      audio.play();
    }
  };

  return { prefs, handleChange, playNotificationSound };
}
